import React from 'react';
import { useTranslation } from 'react-i18next';
import type { AnamnesisRecord, VisitReasonContent } from 'types/anamnesis';
import {
  PatientCard,
  PatientContent,
  PatientDetails,
  PatientHeaderContentCard,
  PatientHeaderSkeletonIcon,
  PatientHeaderSkeletonIconRow,
  PatientHeaderSkeletonNameBar,
} from './PatientHeader.styles';
import { buildPatientNameLine } from 'utils/visitReasonDisplay';
import { PATIENT_NAME_NOT_SPECIFIED } from 'utils/anamnesis';
import { DuePreventionsSection } from 'widgets/preventionAlerts';
import { BesuchsgrundSection, SummarySection } from 'widgets/anamnesis';
import { Accordion } from 'components';
import type { PreventionAlert } from 'types/preventionAlerts';

export type PreventionsScreen = 'patientOverview' | 'recording' | 'result';

export interface PreventionsProps {
  screen: PreventionsScreen;
  defaultOpen?: boolean;
  interactionId?: string;
  hideInfluenzaAlert?: boolean;
  onRecordInfluenza?: (alert: PreventionAlert) => void;
}

interface PatientHeaderProps {
  anamnesis: AnamnesisRecord | null | undefined;
  sticky?: boolean;
  showPreventionAlerts?: boolean;
  preventions?: PreventionsProps;
  visitReason?: VisitReasonContent;
  availableVisitReasons?: VisitReasonContent[];
  onVisitReasonChange?: (visitReason: VisitReasonContent) => void;
  visitReasonAccordionDefaultOpen?: boolean;
  collapsibleSummary?: boolean;
  showSections?: boolean;
}

export const PatientHeader: React.FC<PatientHeaderProps> = ({
  anamnesis,
  sticky = false,
  showPreventionAlerts = false,
  preventions = { screen: 'patientOverview' },
  visitReason,
  availableVisitReasons,
  onVisitReasonChange,
  visitReasonAccordionDefaultOpen = false,
  collapsibleSummary = false,
  showSections = true,
}) => {
  const { patientName, medatixxPatientId, birthdate } = anamnesis || {};

  if (!anamnesis) return null;
  if (!patientName || (!medatixxPatientId && patientName === PATIENT_NAME_NOT_SPECIFIED)) {
    return null;
  }

  const nameLine = buildPatientNameLine(patientName, birthdate, medatixxPatientId);

  const hasVisitReason = showSections && !!visitReason;
  const hasSummary = showSections && !!anamnesis.summary?.sections?.length;

  return (
    <PatientCard $sticky={sticky} role="region" aria-label="Patient data">
      <PatientDetails>
        <PatientContent data-ph-no-autocapture>{nameLine}</PatientContent>
        {showPreventionAlerts && (
          <DuePreventionsSection
            medatixxPatientId={medatixxPatientId}
            interactionId={preventions.interactionId}
            storageKey={preventions.screen}
            defaultOpen={preventions.defaultOpen ?? false}
            hideInfluenzaAlert={preventions.hideInfluenzaAlert ?? false}
            onRecordInfluenza={preventions.onRecordInfluenza}
          />
        )}
        {(hasVisitReason || hasSummary) && (
          <div className="space-y-3">
            {hasVisitReason && (
              <PatientHeaderContentCard>
                <Accordion
                  type="single"
                  collapsible
                  className="w-full"
                  defaultValue={visitReasonAccordionDefaultOpen ? 'visitReason' : undefined}
                >
                  <BesuchsgrundSection
                    visitReason={visitReason}
                    availableVisitReasons={availableVisitReasons}
                    onVisitReasonChange={onVisitReasonChange}
                  />
                </Accordion>
              </PatientHeaderContentCard>
            )}
            {hasSummary && (
              <PatientHeaderContentCard>
                <SummarySection
                  summary={anamnesis.summary}
                  mode="accordion"
                  collapsibleSummary={collapsibleSummary}
                />
              </PatientHeaderContentCard>
            )}
          </div>
        )}
      </PatientDetails>
    </PatientCard>
  );
};

export const PatientHeaderSkeleton: React.FC = () => {
  const { t } = useTranslation();

  return (
    <PatientCard role="status" aria-label={t('general.loading')}>
      <PatientDetails aria-hidden>
        <PatientHeaderSkeletonNameBar />
        <PatientHeaderSkeletonIconRow>
          <PatientHeaderSkeletonIcon />
          <PatientHeaderSkeletonIcon />
        </PatientHeaderSkeletonIconRow>
      </PatientDetails>
    </PatientCard>
  );
};
